import { Form, Formik } from 'formik';
import React, { memo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { LinkProps } from '../../interfaces/LinkProps.interface';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { abbreviateNumber } from '../../utils/abbreviateNumber/abbreviateNumber';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';
import { toNumericFormat } from '../../utils/toNumericFormat/toNumericFormat.util';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import ButtonLink from '../ButtonLink/ButtonLink';
import Col from '../Utils/Col/Col';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import StampDutyFormField from './StampDutyFormField';
import StampDutyFormFieldCheckboxSelect from './StampDutyFormFieldCheckboxSelect';
import FormFieldSingleOptionSelect from './StampDutyFormFieldSingleOptionSelect';
import StampDutyFormFieldTextInput from './StampDutyFormFieldTextInput';
import StampDutyResults from './StampDutyResults';

export interface SbTaxBand {
  from: number;
  to: number;
  rate: number;
}

export interface Table {
  buyer_type: string;
  tax_bands: SbTaxBand[];
  footnote: string;
  threshold_limit: number;
  threshold_table: string;
}

export interface TaxBand {
  taxBand: string;
  rate: number;
  taxableSum: number;
  tax: number;
}

export interface TaxTable {
  taxBands: TaxBand[];
  footnote: string;
  buyingOption: string;
  propertyPrice: number;
}

interface StampDutyCalculatorProps {
  blok: {
    heading: string;
    sub_heading: string;
    results_cta: LinkProps[];

    english_name: string;
    welsh_name: string;
    scottish_name: string;

    english_buyer_options_dropdown: string[];
    welsh_buyer_options_dropdown: string[];
    scottish_buyer_options_dropdown: string[];

    english_tables: Table[];
    welsh_tables: Table[];
    scottish_tables: Table[];
  };
}

export enum CountryType {
  WALES = 'wales',
  ENGLAND = 'england',
  SCOTLAND = 'scotland',
}

export enum BtnLabel {
  CALC = 'calculate',
  RECALC = 'recalculate',
}

const StampDutyCalculator: React.FC<StampDutyCalculatorProps> = ({ blok }) => {
  const {
    heading,
    sub_heading,
    results_cta,

    english_name,
    welsh_name,
    scottish_name,

    english_buyer_options_dropdown,
    welsh_buyer_options_dropdown,
    scottish_buyer_options_dropdown,

    english_tables,
    welsh_tables,
    scottish_tables,
  } = blok;

  const [country, setCountry] = useState<CountryType>(CountryType.ENGLAND);
  const [taxTable, setTaxTable] = useState<TaxTable | null>(null);
  const [btnLabel, setBtnLabel] = useState<string>(BtnLabel.CALC);
  const resultsTable = useRef<HTMLDivElement | null>(null);

  const Buyers = (): string[] => {
    if (country === CountryType.ENGLAND) return english_buyer_options_dropdown;
    if (country === CountryType.WALES) return welsh_buyer_options_dropdown;
    return scottish_buyer_options_dropdown;
  };

  const Tables = (): Table[] => {
    if (country === CountryType.ENGLAND) return english_tables;
    if (country === CountryType.WALES) return welsh_tables;
    return scottish_tables;
  };

  const Name = (): string => {
    if (country === CountryType.ENGLAND) return english_name;
    if (country === CountryType.WALES) return welsh_name;
    return scottish_name;
  };

  const calculateTaxBand = (band: SbTaxBand): string => {
    const fromValue: string = abbreviateNumber(band.from, 1);
    const toValue: string = abbreviateNumber(band.to, 1);

    if (!Number(band.from) && !Number(band.to)) {
      return `£0+`;
    }
    if (!Number(band.from)) {
      return `less than £${toValue}`;
    }
    if (toValue === '') {
      return `over £${fromValue}`;
    }
    return `£${fromValue} to £${toValue}`;
  };

  const calculateTaxableSum = (band: SbTaxBand, price: number): number => {
    const betweenAmount = band.to - band.from;
    const bandFrom = band.from || 0;
    const bandTo = band.to || 999999999;

    if (price < bandFrom) return 0;
    if (price > bandTo) return Number(betweenAmount);
    return Number(price - bandFrom);
  };

  const calculateTax = (amount: number, rate: number): number => {
    return amount * (rate / 100);
  };

  const getTableData = (
    table: Table,
    propertyPrice: number,
    buyingOption: string
  ) => {
    return {
      propertyPrice: propertyPrice,
      footnote: table.footnote,
      buyingOption: buyingOption,
      taxBands: table.tax_bands.map((band: SbTaxBand): TaxBand => {
        const taxableSum = calculateTaxableSum(band, propertyPrice);
        return {
          taxBand: calculateTaxBand(band),
          rate: band.rate,
          taxableSum: Math.round(taxableSum),
          tax: calculateTax(taxableSum, band.rate),
        };
      }),
    };
  };

  const handleSubmit = async (values: any) => {
    const propertyPrice = toNumericFormat(values.price);
    const applicableTable = Tables().find((taxBand) => {
      return taxBand.buyer_type === values.buying_option;
    });

    if (applicableTable) {
      if (
        applicableTable?.threshold_limit &&
        applicableTable?.threshold_limit < values.price
      ) {
        const threshholdTable = Tables()?.find((taxBand) => {
          return taxBand.buyer_type === applicableTable.threshold_table;
        });
        if (threshholdTable) {
          const threshholdTableData = getTableData(
            threshholdTable,
            propertyPrice,
            values.buying_option
          );
          threshholdTableData && setTaxTable(threshholdTableData);
        }
      } else {
        const tableData = getTableData(
          applicableTable,
          propertyPrice,
          values.buying_option
        );
        tableData && setTaxTable(tableData);
      }
      btnLabel === BtnLabel.CALC && setBtnLabel(BtnLabel.RECALC);
      resultsTable?.current?.scrollIntoView();
    }
    setUserBtnClick(userBtnClick + 1);
    pushToDataLayer({
      event: 'stamp_duty_click',
      data: {
        stamp_duty_country: country,
        stamp_duty_category: values.buying_option,
        stam_duty_price: values.price,
        stamp_duty_click: userBtnClick,
      },
    });
  };

  const [userBtnClick, setUserBtnClick] = useState(0);
  const [priceValue, setPriceValue] = useState<string | number>('');

  return (
    <section className="py-28">
      <Container>
        <Grid>
          <Col
            span={{ default: 12, md: 10, lg: 8, xl: 6 }}
            start={{ default: 1, md: 2, lg: 3, xl: 4 }}
          >
            <div className="text-center pb-24 space-y-3">
              <p className="text-[42px] font-dm-serif-display">{heading}</p>
              <p className="text-lg" ref={resultsTable}>
                {sub_heading}
              </p>
            </div>
            <div>
              {taxTable && (
                <StampDutyResults
                  name={Name()}
                  table={taxTable}
                  cta={results_cta}
                />
              )}
              <section>
                <Formik
                  initialValues={{
                    buying_option: '',
                    price: null,
                  }}
                  onSubmit={(e, { resetForm }) => {
                    handleSubmit(e);
                    resetForm();
                    setPriceValue('');
                  }}
                  validationSchema={Yup.object().shape({
                    buying_option: Yup.string().required('Required'),
                    price: Yup.number()
                      .transform((o, v) => v && parseFloat(v.replace(/,/g, '')))
                      .typeError('Price must be a number')
                      .moreThan(0, 'Price must be at least £1')
                      .max(2000000, 'Price must not exceed £2,000,000')
                      .required('Price is required'),
                  })}
                  enableReinitialize={true}
                >
                  <Form className="space-y-10 pt-10">
                    <StampDutyFormField
                      label={'Which country is the property in?'}
                    >
                      <StampDutyFormFieldCheckboxSelect
                        options={[
                          CountryType.ENGLAND,
                          CountryType.WALES,
                          CountryType.SCOTLAND,
                        ]}
                        country={country}
                        setCountry={setCountry}
                      />
                    </StampDutyFormField>

                    <StampDutyFormField label={"I'm buying my"}>
                      <FormFieldSingleOptionSelect
                        name={'buying_option'}
                        options={Buyers()}
                        preselected_option={Buyers()[0]}
                      />
                    </StampDutyFormField>

                    <StampDutyFormField label={'Property Price'}>
                      <StampDutyFormFieldTextInput
                        error_message={''}
                        name={'price'}
                        prefix_value={'£'}
                        validation_rule={''}
                        max_value={100}
                        min_value={100}
                        character_limit={56}
                        character_min={56}
                        fieldValue={priceValue}
                        setFieldValue={setPriceValue}
                      />
                    </StampDutyFormField>
                    <div className="text-center">
                      <ButtonLink
                        url=""
                        label={btnLabel}
                        type={ButtonType.PRIMARY}
                        size={ButtonSize.XL}
                        className="uppercase"
                        btnType="submit"
                      >
                        {btnLabel}
                      </ButtonLink>
                    </div>
                  </Form>
                </Formik>
              </section>
            </div>
          </Col>
        </Grid>
      </Container>
    </section>
  );
};

export default memo(withBlokWrapper(StampDutyCalculator, Theme.ALT));
